/**
 * Validation utilities
 * Shared between server and web applications
 */

/**
 * Validation result
 */
export interface ValidationResult {
	valid: boolean;
	error?: string;
}

/**
 * Check if a value is required (not empty)
 */
export function validateRequired(
	value: string | null | undefined,
	fieldName = 'Field',
): ValidationResult {
	if (value == null || value.trim() === '') {
		return {
			valid: false,
			error: `${fieldName} is required`,
		};
	}

	return { valid: true };
}

/**
 * Validate email format
 */
export function validateEmail(
	email: string | null | undefined,
	required = true,
): ValidationResult {
	if (!required && (email == null || email === '')) {
		return { valid: true };
	}

	if (email == null || email === '') {
		return {
			valid: false,
			error: 'Email is required',
		};
	}

	// Basic email regex
	const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

	if (!emailRegex.test(email)) {
		return {
			valid: false,
			error: 'Invalid email format',
		};
	}

	return { valid: true };
}

/**
 * Validate phone number format
 */
export function validatePhoneNumber(
	phone: string | null | undefined,
	required = false,
): ValidationResult {
	if (!required && (phone == null || phone === '')) {
		return { valid: true };
	}

	if (phone == null || phone === '') {
		return {
			valid: false,
			error: 'Phone number is required',
		};
	}

	// Remove common formatting characters
	const cleaned = phone.replace(/[\s\-\(\)\.]/g, '');

	// Check if it's a valid phone number (10-15 digits)
	if (!/^\+?\d{10,15}$/.test(cleaned)) {
		return {
			valid: false,
			error: 'Invalid phone number format',
		};
	}

	return { valid: true };
}

/**
 * Format phone number to E.164 format
 * @param phone Phone number to format
 * @param defaultCountryCode Default country code (default: '1' for US/Canada)
 * @returns E.164 formatted phone number
 */
export function formatPhoneToE164(
	phone: string,
	defaultCountryCode = '1',
): string {
	// Remove all non-digit characters
	const cleaned = phone.replace(/\D/g, '');

	// If already starts with +, just clean it
	if (phone.startsWith('+')) {
		return '+' + cleaned;
	}

	// If 11 digits and starts with 1 (US/Canada)
	if (cleaned.length === 11 && cleaned[0] === '1') {
		return '+' + cleaned;
	}

	// If 10 digits, add default country code
	if (cleaned.length === 10) {
		return '+' + defaultCountryCode + cleaned;
	}

	// Otherwise, assume it needs the default country code
	return '+' + defaultCountryCode + cleaned;
}

/**
 * Validate phone number is in E.164 format
 */
export function validateE164Phone(phone: string): boolean {
	// E.164 format: +[country code][area code][local phone number]
	const e164Regex = /^\+[1-9]\d{1,14}$/;
	return e164Regex.test(phone);
}

/**
 * Whether a phone field holds no phone number at all, so saving it should
 * remove the stored number rather than try to store what is left.
 *
 * Blank means no digits, or only the digit 1. Stored numbers are E.164 with
 * the US/Canada country code 1 (see `formatPhoneToE164`), so an admin who
 * deletes the digits of "+15551234567" is left with "+1" or a bare "+". Those
 * leftovers are not phone numbers: "+" and "+1" fail E.164 validation on the
 * login server, and a bare "1" would be padded into "+11" and saved. A lone
 * country code can never identify a phone, so treating it as blank loses
 * nothing. Other partial inputs (e.g. "555") are NOT blank and stay subject to
 * normal phone validation.
 *
 * PD-16709.
 */
export function isBlankPhoneNumber(phone: string | null | undefined): boolean {
	if (phone == null) {
		return true;
	}

	const digits = phone.replace(/\D/g, '');

	return digits.length === 0 || digits === '1';
}

/**
 * Strict E.164 normalization: returns the canonical
 * `+<countryCode><nationalNumber>` form, or `null` when the input is
 * null/empty or cannot be parsed into a valid phone shape.
 *
 * This is the strict counterpart to `formatPhoneToE164`. `formatPhoneToE164`
 * always returns a string (best-effort), which is right for form UX but
 * wrong for write-path safety nets: when called on garbage like
 * "see notes" or "555" it would emit "+1see notes" or "+1555", silently
 * corrupting the data. The entity `@BeforeInsert` / `@BeforeUpdate` hooks
 * on `ClientEntity`, `VendorEntity`, `UserEntity`, `ClientPhoneEntity`,
 * and `VendorPhoneEntity` use THIS strict variant: if it returns null,
 * the hook leaves the value alone rather than overwriting it with
 * malformed output.
 *
 * NOTE: a near-identical function lives in `libs/core` as
 * `normalizeToE164` (used by `clientPhoneSync` / `vendorPhoneSync`).
 * Cannot be deduped via re-export because libs/shared cannot import
 * from libs/core (cycle: libs/core imports entity types from
 * libs/shared). Keep both implementations behaviorally identical; if
 * one changes, change the other too.
 */
export function normalizePhoneToE164(
	phone: string | null | undefined,
): string | null {
	if (phone == null) return null;

	const trimmed = phone.trim();
	if (trimmed === '') return null;

	// Drop any trailing extension before collapsing to digits so its digits
	// aren't folded into the number. Extensions are stored separately — see
	// `parsePhoneWithExtension`. PD-14494.
	const { base } = splitPhoneExtension(trimmed);

	const digits = base.replace(/\D/g, '');
	if (digits.length < 10 || digits.length > 15) return null;

	let countryCode: string;
	let nationalNumber: string;

	if (digits.length === 10) {
		countryCode = '1';
		nationalNumber = digits;
	} else if (digits.length === 11 && digits.startsWith('1')) {
		countryCode = '1';
		nationalNumber = digits.slice(1);
	} else {
		countryCode = digits.slice(0, digits.length - 10);
		nationalNumber = digits.slice(-10);
	}

	if (nationalNumber.length !== 10) return null;

	return `+${countryCode}${nationalNumber}`;
}

/**
 * Split a trailing phone extension off the base number. Recognizes the common
 * markers (`ext`, `extension`, `x`, `#`) followed by the extension digits at
 * the end of the string. Returns the base (still un-normalized) and the
 * extension digits (or null when there is no extension). Internal helper for
 * `normalizePhoneToE164` / `parsePhoneWithExtension`. PD-14494.
 */
function splitPhoneExtension(trimmed: string): {
	base: string;
	extension: string | null;
} {
	const match = trimmed.match(
		/^(.*?)\s*(?:ext(?:ension)?\.?|x|#)[\s:.#-]*(\d+)\s*$/i,
	);

	if (match == null) {
		return { base: trimmed, extension: null };
	}

	return { base: match[1] ?? '', extension: match[2] ?? null };
}

/**
 * Parse a raw phone string into its canonical E.164 base number and a separate
 * extension. E.164 cannot encode an extension, so callers that need to retain
 * one store `e164` in the phone column and `extension` in a dedicated column.
 * `e164` is null when the base cannot be parsed into a valid phone shape.
 *
 * NOTE: a behaviorally-identical twin lives in `libs/core`'s `formatters.ts`
 * (`parsePhoneWithExtension`); libs/shared cannot import from libs/core (cycle).
 * Keep both in sync. PD-14494.
 */
export function parsePhoneWithExtension(phone: string | null | undefined): {
	e164: string | null;
	extension: string | null;
} {
	if (phone == null) {
		return { e164: null, extension: null };
	}

	const trimmed = phone.trim();
	if (trimmed === '') {
		return { e164: null, extension: null };
	}

	const { base, extension } = splitPhoneExtension(trimmed);

	return { e164: normalizePhoneToE164(base), extension };
}

/**
 * Validate database alias format (lowercase letters and underscores only)
 */
export function validateDatabaseAlias(
	alias: string | null | undefined,
): ValidationResult {
	if (alias == null || alias === '') {
		return {
			valid: false,
			error: 'Alias is required',
		};
	}

	if (!/^[a-z_]+$/.test(alias)) {
		return {
			valid: false,
			error: 'Alias must contain only lowercase letters and underscores',
		};
	}

	if (alias.length < 3) {
		return {
			valid: false,
			error: 'Alias must be at least 3 characters',
		};
	}

	if (alias.length > 50) {
		return {
			valid: false,
			error: 'Alias must be less than 50 characters',
		};
	}

	return { valid: true };
}

/**
 * Validate number is within range
 */
export function validateNumberRange(
	value: number | null | undefined,
	min?: number,
	max?: number,
	fieldName = 'Value',
): ValidationResult {
	if (value == null) {
		return {
			valid: false,
			error: `${fieldName} is required`,
		};
	}

	if (min !== undefined && value < min) {
		return {
			valid: false,
			error: `${fieldName} must be at least ${min}`,
		};
	}

	if (max !== undefined && value > max) {
		return {
			valid: false,
			error: `${fieldName} must be at most ${max}`,
		};
	}

	return { valid: true };
}

/**
 * Validate string length
 */
export function validateLength(
	value: string | null | undefined,
	minLength?: number,
	maxLength?: number,
	fieldName = 'Field',
): ValidationResult {
	if (value == null) {
		return {
			valid: false,
			error: `${fieldName} is required`,
		};
	}

	const length = value.length;

	if (minLength !== undefined && length < minLength) {
		return {
			valid: false,
			error: `${fieldName} must be at least ${minLength} characters`,
		};
	}

	if (maxLength !== undefined && length > maxLength) {
		return {
			valid: false,
			error: `${fieldName} must be at most ${maxLength} characters`,
		};
	}

	return { valid: true };
}

/**
 * Validate URL format
 */
export function validateUrl(
	url: string | null | undefined,
	required = true,
): ValidationResult {
	if (!required && (url == null || url === '')) {
		return { valid: true };
	}

	if (url == null || url === '') {
		return {
			valid: false,
			error: 'URL is required',
		};
	}

	try {
		new URL(url);
		return { valid: true };
	} catch {
		return {
			valid: false,
			error: 'Invalid URL format',
		};
	}
}

/**
 * Validate multiple fields at once
 */
export function validateForm<T extends Record<string, any>>(
	data: T,
	validators: Partial<Record<keyof T, (value: any) => ValidationResult>>,
): {
	valid: boolean;
	errors: Partial<Record<keyof T, string>>;
} {
	const errors: Partial<Record<keyof T, string>> = {};

	for (const [field, validator] of Object.entries(validators)) {
		if (validator) {
			const result = (validator as (value: any) => ValidationResult)(
				data[field as keyof T],
			);

			if (!result.valid && result.error) {
				errors[field as keyof T] = result.error;
			}
		}
	}

	return {
		valid: Object.keys(errors).length === 0,
		errors,
	};
}
